import { useState } from "react";
import { History, RefreshCw } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useResourceActionList } from "@/hooks/useResourceQueries";
import { getPaginationItems } from "@/lib/pagination";
import type { ResourceActionListItem } from "@/types/admin";
import { ResourceActionDetailDialog } from "./ResourceActionDetailDialog";
import { ResourceActionTable } from "./ResourceActionTable";

/** Module-level so the table gets the same array on every render. */
const OMITTED_COLUMNS = ["tenant", "user"] as const;

interface ResourceActionsTabProps {
  resourceId: string;
}

/**
 * The resource-detail tab listing the `resource_actions` rows of one resource, newest first, from
 * `GET /api/v1/admin/resource-actions?resourceId=…` — paginated, and seeking the
 * `(resource_id, created_at DESC)` index.
 *
 * No filters: the tab is already scoped to one machine, and a type or status filter was declined for
 * this trail (2026-09-11). No column menu either: the column set is fixed, and the table scrolls
 * horizontally when it does not fit. Tenant and User are left out (2026-10-07) — every row here
 * belongs to the machine on screen, and both are still in the row's detail dialog. Page and page size
 * are local state rather than a persisted table store — they describe one visit to one resource, not
 * a view an operator sets up and comes back to.
 */
export function ResourceActionsTab({ resourceId }: ResourceActionsTabProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedAction, setSelectedAction] =
    useState<ResourceActionListItem | null>(null);

  const {
    data: response,
    isFetching,
    error,
    refetch,
  } = useResourceActionList({ resourceId, page, pageSize });

  const actions = response?.data ?? [];

  // Fetching with nothing on screen yet: skeleton rows. With rows in hand the pill takes over.
  const isReloading = isFetching && actions.length === 0;
  const totalPages = response?.pagination?.totalPages ?? 1;
  const totalCount = response?.pagination?.totalItems ?? 0;

  return (
    <Card className="relative border-border/80 shadow-none">
      {/* Anchored to the Card, not the viewport. */}
      {isFetching && !isReloading && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
            <RefreshCw className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Updating...</span>
          </div>
        </div>
      )}

      <CardHeader>
        <CardTitle>Resource Actions</CardTitle>
        <CardDescription>
          Operations run against this resource and how they ended, newest first
        </CardDescription>
      </CardHeader>

      {/* min-w-0 so the card keeps the page's width and the table scrolls inside it, instead of the
          table's full width pushing the card — and the page — wider. */}
      <CardContent className="flex min-w-0 flex-col">
        {error && !response ? (
          // Retry first: one failed request should not send the operator back to the list.
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <p className="text-destructive-ink">
              Failed to load resource actions
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              disabled={isFetching}
            >
              {isFetching && <Spinner className="mr-2" />}
              Retry
            </Button>
          </div>
        ) : (
          <>
            {/* Reached only with a previous result in hand. */}
            {error && (
              <div className="mb-2 flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
                <p className="text-sm text-destructive-ink">
                  Could not refresh the list. Showing the last result that
                  loaded.
                </p>
                <Button
                  onClick={() => refetch()}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  className="h-7 shrink-0 px-2 text-xs"
                >
                  {isFetching && <Spinner className="mr-1.5 h-3 w-3" />}
                  Retry
                </Button>
              </div>
            )}

            {isReloading ? (
              <div className="space-y-3 rounded-md border p-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-12 w-full" />
                ))}
              </div>
            ) : actions.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <History className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">
                  No resource actions yet
                </h3>
                <p className="text-sm text-muted-foreground">
                  Provisioning, power and configuration actions on this resource
                  will appear here
                </p>
              </div>
            ) : (
              // Height-bounded so the table scrolls inside the card on both axes and its sticky
              // header has a scrollport to stick to.
              <div className="flex max-h-[70vh] min-h-0 min-w-0 flex-col">
                <ResourceActionTable
                  actions={actions}
                  startIndex={(page - 1) * pageSize}
                  onViewDetails={setSelectedAction}
                  showAllColumns
                  omitColumns={OMITTED_COLUMNS}
                />
              </div>
            )}

            {!isReloading && (
              <div className="flex flex-col items-center gap-2 pt-3 sm:flex-row sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages} ({totalCount} total)
                </p>
                <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      setPageSize(parseInt(value, 10));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[120px] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 per page</SelectItem>
                      <SelectItem value="20">20 per page</SelectItem>
                      <SelectItem value="50">50 per page</SelectItem>
                      <SelectItem value="100">100 per page</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1 overflow-x-auto">
                    <Button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1 || isFetching}
                      variant="outline"
                      size="sm"
                    >
                      Previous
                    </Button>
                    {getPaginationItems(page, totalPages).map((item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={page === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                    )}
                    <Button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page >= totalPages || isFetching}
                      variant="outline"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>

                  {/* Nothing to reset on this tab, so the icon only reloads. */}
                  <Button
                    onClick={() => void refetch()}
                    variant="outline"
                    size="sm"
                    disabled={isFetching}
                    title="Reload"
                    aria-label="Reload"
                    className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {selectedAction && (
        <ResourceActionDetailDialog
          action={selectedAction}
          open={selectedAction !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedAction(null);
          }}
        />
      )}
    </Card>
  );
}
